import { useState, useEffect } from "react";
import axios from "axios";
import config from '../config/config';

const useProductData = (categoryName, partNumber, filters, searchInput, itemcode, restrictByParentIds = null) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const trimmedSearch = String(searchInput ?? "").trim();
    const hasNonEmptyFilters = Array.isArray(filters) && filters.length > 0;
    const isGlobalSuggestionOnly =
      !categoryName &&
      !partNumber &&
      !itemcode &&
      !hasNonEmptyFilters;

    if (isGlobalSuggestionOnly && !trimmedSearch) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const fetchData = async () => {
      try {
        const filteredData = await getProductsApi(
          categoryName,
          filters,
          searchInput,
          partNumber,
          itemcode,
          restrictByParentIds
        );
        setData(filteredData ?? []);
      } catch (error) {
        console.error("useProductData: search failed", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceFetch = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(debounceFetch);
  }, [categoryName, partNumber, filters, searchInput, itemcode, restrictByParentIds]);

  const getProductsApi = async (categoryName, filters, searchInput, partNumber, itemcode, restrictByParentIds) => {
    let searchFilters = [];

    let categoryCode = null;
    if (categoryName) {
      try {
        const groupResponse = await axios.get(`${config.API_BASE_URL}/group/${categoryName}/id`);
        categoryCode = groupResponse.data.groupId;
      } catch (error) {
        console.error('Error fetching category code:', error);
      }
    }

    if (filters) {
      searchFilters = [...filters];
    }

    const trimmedSearch = String(searchInput ?? "").trim();
    if (trimmedSearch) {
      searchFilters.push({
        columnName: "Search",
        value: trimmedSearch,
        operator: "contains",
      });
    }

    // if (partNumber) {
    //   searchFilters.push({
    //     columnName: "Part Number",
    //     value: partNumber,
    //     operator: "eq"
    //   });
    // }

    if (itemcode) {
      searchFilters.push({
        columnName: "ItemCode",
        value: itemcode,
        operator: "eq"
      });
    }

    if (categoryCode && categoryCode !== -1) {
      searchFilters.push({
        columnName: "Group",
        value: categoryCode,
        operator: "eq"
      });
    }

    if (Array.isArray(restrictByParentIds) && restrictByParentIds.length > 0 && !categoryCode) {
      try {
        const idsParam = restrictByParentIds.join(",");
        const tilesResponse = await axios.get(`${config.API_BASE_URL}/tiles/${idsParam}`);
        const childIds = (tilesResponse.data ?? [])
          .flatMap((parent) => parent?.Children ?? [])
          .map((child) => child?.id)
          .filter((id) => id != null);

        if (childIds.length > 0) {
          searchFilters.push({
            columnName: "Group",
            value: childIds.map(String),
            operator: "in",
          });
        }
      } catch (error) {
        console.error("useProductData: failed to resolve parent category children", error);
      }
    }

    const response = await axios.post(`${config.API_BASE_URL}/search`, searchFilters);
    return response.data;
  };

  return { data, loading };
};

export default useProductData;
